"use client";
import { useState, useEffect } from "react";
import { Country } from "country-state-city";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { getCleanedStates, getCleanedCities } from "@/utils/locationMapper";
import { useTranslation } from "@/hooks/useLanguage";

/**
 * Reusable Location Edit Fields Component
 *
 * Usage:
 * <LocationEditFields
 *   initialData={{
 *     country: "Saudi Arabia",
 *     countryCode: "SA",
 *     state: "Riyadh",
 *     stateCode: "01",
 *     city: "Riyadh"
 *   }}
 *   onChange={(locationData) => console.log(locationData)}
 * />
 */
export default function LocationEditFields({ initialData = {}, onChange }) {
  const { t } = useTranslation();

  // Initialize with stored data - now directly using codes!
  const [locationState, setLocationState] = useState({
    country: initialData.country || "",
    countryCode: initialData.countryCode || "",
    state: initialData.state || "",
    stateCode: initialData.stateCode || "",
    city: initialData.city || "",
  });

  // Dropdown open states
  const [openCountry, setOpenCountry] = useState(false);
  const [openState, setOpenState] = useState(false);
  const [openCity, setOpenCity] = useState(false);

  // Get data
  const allCountries = Country.getAllCountries();
  const allStates = getCleanedStates(locationState.countryCode);
  const allCities = getCleanedCities(
    locationState.countryCode,
    locationState.stateCode,
  );

  useEffect(() => {
    setLocationState({
      country: initialData.country || "",
      countryCode: initialData.countryCode || "",
      state: initialData.state || "",
      stateCode: initialData.stateCode || "",
      city: initialData.city || "",
    });
  }, [initialData]);

  const handleCountryChange = (country) => {
    const newState = {
      country: country.name,
      countryCode: country.isoCode,
      state: "",
      stateCode: "",
      city: "",
    };
    setLocationState(newState);
    if (onChange) {
      onChange(newState);
    }
    setOpenCountry(false);
  };

  const handleStateChange = (state) => {
    const newState = {
      ...locationState,
      state: state.name,
      stateCode: state.isoCode,
      city: "",
    };
    setLocationState(newState);
    if (onChange) {
      onChange(newState);
    }
    setOpenState(false);
  };

  const handleCityChange = (cityName) => {
    const newState = {
      ...locationState,
      city: cityName,
    };
    setLocationState(newState);
    if (onChange) {
      onChange(newState);
    }
    setOpenCity(false);
  };

  return (
    <div className="space-y-4">
      {/* Country Dropdown */}
      <div className="space-y-2">
        <Label required>{t("common.country")}</Label>
        <Popover open={openCountry} onOpenChange={setOpenCountry}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={openCountry}
              className={cn(
                "w-full justify-between",
                !locationState.country && "text-muted-foreground",
              )}
            >
              {locationState.country
                ? `${
                    allCountries.find((c) => c.name === locationState.country)
                      ?.flag || ""
                  } ${locationState.country}`
                : t("common.select_a_country")}
              <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-full p-0 z-[100]"
            align="start"
            onWheel={(e) => e.stopPropagation()}
          >
            <Command onWheel={(e) => e.stopPropagation()}>
              <CommandInput
                placeholder={t("location.placeholder.search_country")}
              />
              <CommandList>
                <CommandEmpty>
                  {t("location.messages.no_country_found")}
                </CommandEmpty>
                <CommandGroup>
                  <ScrollArea
                    className="h-72"
                    onWheel={(e) => e.stopPropagation()}
                  >
                    {allCountries.map((country) => (
                      <CommandItem
                        key={country.isoCode}
                        value={`${country.name} ${country.isoCode}`}
                        onSelect={() => handleCountryChange(country)}
                      >
                        <Check
                          className={cn(
                            "me-2 h-4 w-4",
                            locationState.country === country.name
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                        {country.flag} {country.name}
                      </CommandItem>
                    ))}
                  </ScrollArea>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* State Dropdown */}
      <div className="space-y-2">
        <Label required>{t("location.label.state")}</Label>
        {locationState.countryCode && allStates.length > 0 ? (
          <Popover open={openState} onOpenChange={setOpenState}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={openState}
                className={cn(
                  "w-full justify-between",
                  !locationState.state && "text-muted-foreground",
                )}
              >
                {locationState.state || t("location.placeholder.select_state")}
                <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-full p-0 z-[100]"
              align="start"
              onWheel={(e) => e.stopPropagation()}
            >
              <Command onWheel={(e) => e.stopPropagation()}>
                <CommandInput
                  placeholder={t("location.placeholder.search_state")}
                />
                <CommandList>
                  <CommandEmpty>
                    {t("location.messages.no_state_found")}
                  </CommandEmpty>
                  <CommandGroup>
                    <ScrollArea
                      className="h-72"
                      onWheel={(e) => e.stopPropagation()}
                    >
                      {allStates.map((state) => (
                        <CommandItem
                          key={state.isoCode}
                          value={state.name}
                          onSelect={() => handleStateChange(state)}
                        >
                          <Check
                            className={cn(
                              "me-2 h-4 w-4",
                              locationState.state === state.name
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                          {state.name}
                        </CommandItem>
                      ))}
                    </ScrollArea>
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        ) : (
          <Input
            placeholder={
              !locationState.countryCode
                ? t("location.placeholder.select_country_first")
                : t("location.placeholder.enter_state")
            }
            value={locationState.state}
            onChange={(e) => {
              const newState = {
                ...locationState,
                state: e.target.value,
                stateCode: "",
              };
              setLocationState(newState);
              if (onChange) {
                onChange(newState);
              }
            }}
            disabled={!locationState.countryCode}
          />
        )}
        {locationState.countryCode && allStates.length === 0 && (
          <p className="text-xs text-muted-foreground mt-1">
            {t("location.messages.no_states_found")}
          </p>
        )}
      </div>

      {/* City Dropdown */}
      <div className="space-y-2">
        <Label required>{t("common.city")}</Label>
        {locationState.countryCode &&
        locationState.stateCode &&
        allCities.length > 0 ? (
          <Popover open={openCity} onOpenChange={setOpenCity}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={openCity}
                className={cn(
                  "w-full justify-between",
                  !locationState.city && "text-muted-foreground",
                )}
              >
                {locationState.city || t("location.placeholder.select_city")}
                <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-full p-0 z-[100]"
              align="start"
              onWheel={(e) => e.stopPropagation()}
            >
              <Command onWheel={(e) => e.stopPropagation()}>
                <CommandInput
                  placeholder={t("location.placeholder.search_city")}
                />
                <CommandList>
                  <CommandEmpty>
                    {t("location.messages.no_city_found")}
                  </CommandEmpty>
                  <CommandGroup>
                    <ScrollArea
                      className="h-72"
                      onWheel={(e) => e.stopPropagation()}
                    >
                      {allCities.map((city, index) => (
                        <CommandItem
                          key={`${city.name}-${index}`}
                          value={city.name}
                          onSelect={handleCityChange}
                        >
                          <Check
                            className={cn(
                              "me-2 h-4 w-4",
                              locationState.city === city.name
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                          {city.name}
                        </CommandItem>
                      ))}
                    </ScrollArea>
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        ) : (
          <Input
            placeholder={
              !locationState.countryCode
                ? t("location.placeholder.select_country_first")
                : t("location.placeholder.enter_city_name")
            }
            value={locationState.city}
            onChange={(e) => {
              const newState = {
                ...locationState,
                city: e.target.value,
              };
              setLocationState(newState);
              if (onChange) {
                onChange(newState);
              }
            }}
            disabled={!locationState.countryCode}
          />
        )}
        {locationState.countryCode &&
          locationState.stateCode &&
          allCities.length === 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              {t("location.messages.no_cities_found")}
            </p>
          )}
      </div>
    </div>
  );
}
